import { useCallback } from 'react'
import { NormalFormSelectionField, NormalFormTextField } from '../molecules'
import {
  ManagerRatingsReportFilterInput,
  OrderDirectionOption,
  PageSizeOption,
} from '@/graphql/generated'
import { useUserLanguage } from '@/customHooks'
import { orderDirectionMapper, pageSizeMapper } from '@/helpers'
import { Button } from '../atoms'

type ManagerRatingsFiltersProps = {
  filters: ManagerRatingsReportFilterInput
  onFilterChange: (newFilters: ManagerRatingsReportFilterInput) => void
}

const texts = {
  es: {
    search: 'Buscar',
    searchPlaceholder: 'Buscar por inquilino o propiedad...',
    month: 'Mes',
    rating: 'Calificación',
    order: 'Orden',
    pageSize: 'Registros por página',
    clearFilters: 'Limpiar Filtros',
    all: 'Todos',
  },
  en: {
    search: 'Search',
    searchPlaceholder: 'Search by tenant or property...',
    month: 'Month',
    rating: 'Rating',
    order: 'Order',
    pageSize: 'Page size',
    clearFilters: 'Clear Filters',
    all: 'All',
  },
}

const initialFilters: ManagerRatingsReportFilterInput = {
  month: null,
  orderDirection: OrderDirectionOption.Desc,
  pageSize: PageSizeOption.TwentyFive,
  page: null,
  ratingValue: null,
  search: null,
}

export const ManagerRatingsFilters = ({
  filters,
  onFilterChange,
}: ManagerRatingsFiltersProps) => {
  const userLanguage = useUserLanguage()
  const t = userLanguage === 'es' ? texts.es : texts.en

  const monthOptions = Array.from({ length: 12 }, (_, index) => {
    const month = index + 1
    return { name: month.toString(), value: month.toString() }
  })

  const ratingOptions = Array.from({ length: 5 }, (_, index) => {
    const rating = index + 1
    return {
      name: `${rating} ${userLanguage === 'es' ? 'estrellas' : 'stars'}`,
      value: rating.toString(),
    }
  })

  const orderDirectionOptions = Object.values(OrderDirectionOption).map((value) => ({
    name: orderDirectionMapper(value, userLanguage),
    value,
  }))

  const pageSizeOptions = [
    PageSizeOption.TwentyFive,
    PageSizeOption.Fifty,
    PageSizeOption.SeventyFive,
    PageSizeOption.OneHundred,
  ].map((value) => ({
    name: pageSizeMapper(value, userLanguage),
    value,
  }))

  const handleFieldChange = useCallback(
    (field: keyof ManagerRatingsReportFilterInput, value: string | null) => {
      if (field === 'month' || field === 'ratingValue') {
        onFilterChange({
          ...filters,
          [field]: value ? Number(value) : null,
        })
        return
      }
      onFilterChange({
        ...filters,
        [field]: value === '' ? null : value,
      })
    },
    [filters, onFilterChange]
  )

  return (
    <div className="bg-white p-6 rounded-xl mb-8">
      <div className="grid grid-cols-1 md:grid-cols-10 gap-4 items-end">
        <div className="md:col-span-4">
          <NormalFormTextField
            label={t.search}
            name="search"
            placeholder={t.searchPlaceholder}
            value={filters.search || ''}
            onChange={(value) => handleFieldChange('search', value)}
          />
        </div>
        <div className="md:col-span-2">
          <NormalFormSelectionField<string>
            label={t.month}
            name="month"
            options={monthOptions}
            value={filters.month?.toString() ?? null}
            onChange={(value) => handleFieldChange('month', value)}
            noValueText={t.all}
          />
        </div>
        <div className="md:col-span-2">
          <NormalFormSelectionField<string>
            label={t.rating}
            name="ratingValue"
            options={ratingOptions}
            value={filters.ratingValue?.toString() ?? null}
            onChange={(value) => handleFieldChange('ratingValue', value)}
            noValueText={t.all}
          />
        </div>
        <div className="md:col-span-1">
          <NormalFormSelectionField<OrderDirectionOption>
            label={t.order}
            name="orderDirection"
            options={orderDirectionOptions}
            value={filters.orderDirection}
            onChange={(value) => handleFieldChange('orderDirection', value)}
          />
        </div>
        <div className="md:col-span-1">
          <NormalFormSelectionField<PageSizeOption>
            label={t.pageSize}
            name="pageSize"
            options={pageSizeOptions}
            value={filters.pageSize}
            onChange={(value) => handleFieldChange('pageSize', value)}
          />
        </div>
      </div>
      <div className="flex justify-end mt-4">
        <Button secondary onClick={() => onFilterChange(initialFilters)}>
          {t.clearFilters}
        </Button>
      </div>
    </div>
  )
}
